/* Admin Center — Roles & Privileges tab (extracted from AdminPage.tsx) */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Check, ChevronRight, Minus, Search, Trash2, X } from 'lucide-react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import EmptyState from '../../components/EmptyState';
import { AdminService } from '../../services/adminService';
import type { Role } from '../../../shared/types';
import type { AppModule } from '../../../shared/modules';
import {
  PERMISSION_CATEGORIES,
  ROLE_PRESETS,
  BASE_PERMISSIONS,
  permissionsToLegacy,
  ALL_PERMISSIONS,
  isPermissionInModules,
} from '../../../shared/permissions';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import { inputStyle, btnPrimary, btnDanger, RowSkeleton } from './shared';

/* ─── Roles Tab ─── */
function RolesTab({
  toast,
  enabledModules,
}: {
  toast: AdminPageProps['toast'];
  enabledModules?: AppModule[];
}) {
  // Categories whose module the firm hasn't licensed are hidden from the
  // builder (R3.4 — out-of-tier keys evaluate false server-side anyway, so
  // offering them here would just be a toggle that lies). An unseeded firm
  // (no enabledModules) shows everything, matching the server's all-enabled
  // fallback.
  const visibleCategories = PERMISSION_CATEGORIES.filter((cat) =>
    isPermissionInModules(cat.permissions[0]?.key ?? `${cat.id}.unknown`, enabledModules)
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [editing, setEditing] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Editor UI state — kept here so search query and expand state survive
  // checkbox toggles without re-rendering the whole permission catalog.
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);

  // Reset transient editor state whenever we open/close a different role.
  useEffect(() => {
    setSearchQuery('');
    setExpandedCats(new Set());
    setPendingPreset(null);
  }, [editing?.id]);

  useEffect(() => {
    load();
  }, []);
  useTableSync(['roles'], () => load());
  async function load() {
    try {
      const all = await AdminService.listRoles();
      all.sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));
      setRoles(all);
    } finally {
      setLoading(false);
    }
  }

  function empty(): Role {
    const perms = [
      ...BASE_PERMISSIONS,
      'specs.view',
      'specs.create',
      'specs.edit_own',
      'team_members.view',
      'tasks.edit_own',
      'files.upload',
    ];
    return {
      id: '',
      name: '',
      accessLevel: 'member',
      permissions: perms,
      ...permissionsToLegacy(perms),
      sortOrder: roles.length,
      createdAt: '',
      updatedAt: '',
    };
  }

  async function save() {
    if (!editing || saving) return;
    if (!editing.name.trim()) {
      toast('Enter a role name', true);
      return;
    }
    setSaving(true);
    try {
      const legacy = permissionsToLegacy(editing.permissions || []);
      const toSave = { ...editing, ...legacy };
      await AdminService.saveRole(toSave);
      await load();
      setEditing(null);
      toast('Role saved');
    } catch (err: any) {
      console.error('[RolesTab] Save failed:', err);
      toast('Save failed: ' + (err?.message || String(err)), true);
    } finally {
      setSaving(false);
    }
  }

  // The Site Admin role (accessLevel 'site_admin', or any role carrying the
  // '*' wildcard) is managed by SpecForma — the server refuses edit/delete
  // on it (R3.1), so the UI must not offer either.
  function isRequired(role: Role | null | undefined): boolean {
    if (!role) return false;
    return role.accessLevel === 'site_admin' || (role.permissions || []).includes('*');
  }

  async function remove(id: string) {
    const role = roles.find((r) => r.id === id);
    if (role && isRequired(role)) {
      toast('This is a required role and cannot be deleted.', true);
      return;
    }
    if (!confirm('Delete this role? Team members assigned to it will have their role unset.'))
      return;
    try {
      await AdminService.deleteRole(id);
      if (editing?.id === id) setEditing(null);
      await load();
      toast('Role deleted');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  function togglePerm(key: string) {
    setEditing((prev) => {
      if (!prev) return prev;
      const perms = prev.permissions || [];
      const next = perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];
      return { ...prev, permissions: next };
    });
  }

  /** Click handler for preset chips. For brand-new roles (no persisted id)
   *  we apply silently because there's nothing to clobber yet. For existing
   *  roles, surface a diff-aware confirm bar so a stray click can't wipe
   *  out hand-tuned permissions. */
  function requestPreset(presetId: string) {
    const preset = ROLE_PRESETS[presetId];
    if (!preset || !editing) return;
    const current = new Set(editing.permissions || []);
    const target = new Set(preset.permissions);
    const sameSize = current.size === target.size;
    const sameContents = sameSize && [...current].every((k) => target.has(k));
    if (sameContents) return; // already on this preset
    if (!editing.id) {
      // Fresh role — no risk of clobbering meaningful work.
      setEditing({ ...editing, permissions: [...preset.permissions] });
      return;
    }
    setPendingPreset(presetId);
  }

  function confirmPendingPreset() {
    if (!pendingPreset || !editing) return;
    const preset = ROLE_PRESETS[pendingPreset];
    if (!preset) return;
    setEditing({ ...editing, permissions: [...preset.permissions] });
    setPendingPreset(null);
  }

  function expandAllCats() {
    setExpandedCats(new Set(visibleCategories.map((c) => c.id)));
  }
  function collapseAllCats() {
    setExpandedCats(new Set());
  }
  function toggleCatExpansion(catId: string) {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  }

  function toggleCategory(catId: string) {
    if (!editing) return;
    const cat = PERMISSION_CATEGORIES.find((c) => c.id === catId);
    if (!cat) return;
    const perms = editing.permissions || [];
    const allEnabled = cat.permissions.every((p) => perms.includes(p.key));
    if (allEnabled) {
      // Remove all in category (except base permissions)
      const catKeys = new Set(cat.permissions.map((p) => p.key));
      const baseSet = new Set(BASE_PERMISSIONS);
      setEditing({
        ...editing,
        permissions: perms.filter((p) => !catKeys.has(p) || baseSet.has(p)),
      });
    } else {
      // Add all in category
      const next = new Set(perms);
      cat.permissions.forEach((p) => next.add(p.key));
      setEditing({ ...editing, permissions: [...next] });
    }
  }

  const checkboxStyle = (checked: boolean): React.CSSProperties => ({
    width: 'var(--space-4)',
    height: 'var(--space-4)',
    borderRadius: rem(3),
    flexShrink: 0,
    border: `2px solid ${checked ? 'var(--ac)' : 'var(--bd)'}`,
    background: checked ? 'var(--ac)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-inverse)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
  });

  if (editing) {
    const readOnly = isRequired(editing);
    // Site Admin's grant comes from the server-side bypass, not its stored row
    const perms = readOnly ? ALL_PERMISSIONS : editing.permissions || [];
    return (
      <div style={{ maxWidth: rem(700) }}>
        <button
          onClick={() => setEditing(null)}
          style={{
            ...btnDanger,
            display: 'inline-flex',
            alignItems: 'center',
            gap: rem(6),
            marginBottom: 'var(--space-3)',
            color: 'var(--tm)',
            border: '1px solid var(--bd)',
          }}
        >
          <Icon icon={ArrowLeft} em={1.0833} /> Back to Roles
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <h3
            style={{
              fontSize: 'calc(var(--font-size-base) * 15 / 13)',
              fontWeight: 700,
              margin: 0,
              flex: 1,
            }}
          >
            {editing.id ? 'Edit Role' : 'New Role'}
          </h3>
          {!readOnly && (
            <button
              onClick={save}
              disabled={saving}
              style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving...' : 'Save Role'}
            </button>
          )}
          {editing.id && !readOnly && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>

        {readOnly && (
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              marginBottom: 'var(--space-4)',
              padding: `${rem(10)} var(--space-3)`,
              background: 'var(--sa)',
              borderRadius: 'var(--radius-md)',
              lineHeight: 1.5,
            }}
          >
            Site Admin is managed by SpecForma and always holds every permission. It cannot be
            renamed, edited, or deleted.
          </p>
        )}

        <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <label style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Role Name
            </div>
            <input
              value={editing.name}
              disabled={readOnly}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
              style={{ ...inputStyle, opacity: readOnly ? 0.6 : 1 }}
            />
          </label>
          <label style={{ width: rem(100) }}>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Sort Order
            </div>
            <input
              type="number"
              min="0"
              value={editing.sortOrder}
              disabled={readOnly}
              onChange={(e) =>
                setEditing((prev) =>
                  prev ? { ...prev, sortOrder: parseInt(e.target.value) || 0 } : prev
                )
              }
              style={{ ...inputStyle, opacity: readOnly ? 0.6 : 1 }}
            />
          </label>
        </div>

        {/* \u2500\u2500\u2500 Quick Presets \u2500\u2500\u2500 */}
        {/* Presets are clone-seeds: every role except Site Admin is
            firm-built, so this section only ever offers a starting point \u2014
            never a real "this role IS the preset" identity. */}
        {!readOnly &&
          (() => {
            const currentSet = new Set(perms);
            const matchingPresetId = Object.entries(ROLE_PRESETS).find(([, preset]) => {
              const t = new Set(preset.permissions);
              return t.size === currentSet.size && [...currentSet].every((k) => t.has(k));
            })?.[0];
            return (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: rem(6),
                  }}
                >
                  <div
                    style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tm)' }}
                  >
                    {editing.id
                      ? 'Clone a preset into this role'
                      : 'Start from a preset (clone-seed)'}
                  </div>
                  {matchingPresetId && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        fontStyle: 'italic',
                      }}
                    >
                      started from {ROLE_PRESETS[matchingPresetId].label}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: rem(6), flexWrap: 'wrap' }}>
                  {Object.entries(ROLE_PRESETS).map(([id, preset]) => {
                    const isCurrent = matchingPresetId === id;
                    return (
                      <button
                        key={id}
                        onClick={() => requestPreset(id)}
                        disabled={isCurrent}
                        title={
                          isCurrent
                            ? 'Already matches this preset'
                            : `Clone ${preset.permissions.length} permissions from ${preset.label} into this firm-built role`
                        }
                        style={{
                          padding: `${rem(5)} var(--space-3)`,
                          borderRadius: rem(999),
                          border: `1px solid ${isCurrent ? 'var(--ac)' : 'var(--bd)'}`,
                          background: isCurrent
                            ? 'color-mix(in srgb, var(--ac) 12%, transparent)'
                            : 'var(--sa)',
                          color: isCurrent ? 'var(--ac)' : 'var(--tx)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: isCurrent ? 700 : 500,
                          cursor: isCurrent ? 'default' : 'pointer',
                          transition: 'all .12s',
                        }}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

        {/* \u2500\u2500\u2500 Pending preset confirm bar \u2500\u2500\u2500 */}
        {pendingPreset &&
          (() => {
            const preset = ROLE_PRESETS[pendingPreset];
            if (!preset) return null;
            const current = new Set(perms);
            const target = new Set(preset.permissions);
            const adding = preset.permissions.filter((k) => !current.has(k)).length;
            const removing = perms.filter((k) => !target.has(k)).length;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: `var(--space-3) ${rem(14)}`,
                  marginBottom: 'var(--space-4)',
                  background: 'color-mix(in srgb, var(--ac) 8%, transparent)',
                  border: '1px solid var(--ac)',
                  borderRadius: rem(8),
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      color: 'var(--tx)',
                      marginBottom: rem(2),
                    }}
                  >
                    Replace current permissions with {preset.label}?
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                    +{adding} added, -{removing} removed.{' '}
                    {removing > 0 ? 'This cannot be undone after saving.' : ''}
                  </div>
                </div>
                <button
                  onClick={() => setPendingPreset(null)}
                  style={{
                    padding: `${rem(6)} ${rem(14)}`,
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--bd)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPendingPreset}
                  style={{
                    padding: `${rem(6)} ${rem(14)}`,
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: 'var(--ac)',
                    color: 'var(--color-text-inverse)',
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Replace
                </button>
              </div>
            );
          })()}

        {/* \u2500\u2500\u2500 Permissions header \u2500\u2500\u2500 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-2)',
          }}
        >
          <div
            style={{
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              fontWeight: 700,
              color: 'var(--tx)',
            }}
          >
            Permissions{' '}
            <span style={{ fontWeight: 400, color: 'var(--tm)' }}>
              ({perms.filter((p) => ALL_PERMISSIONS.includes(p)).length} of {ALL_PERMISSIONS.length}
              )
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={expandAllCats}
            style={{
              padding: `var(--space-1) ${rem(10)}`,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tm)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            Expand all
          </button>
          <button
            onClick={collapseAllCats}
            style={{
              padding: `var(--space-1) ${rem(10)}`,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tm)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            Collapse all
          </button>
        </div>

        {/* \u2500\u2500\u2500 Search \u2500\u2500\u2500 */}
        <div style={{ position: 'relative', marginBottom: 'var(--space-3)' }}>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search permissions by name, description, or key..."
            style={{
              ...inputStyle,
              paddingLeft: 'var(--space-6)',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: rem(10),
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--tm)',
              display: 'inline-flex',
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <Icon icon={Search} size="md" />
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              title="Clear search"
              aria-label="Clear search"
              style={{
                position: 'absolute',
                right: 'var(--space-1)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--tm)',
                cursor: 'pointer',
                padding: rem(9),
                lineHeight: 1,
                display: 'inline-flex',
              }}
            >
              <Icon icon={X} em={1.0769} />
            </button>
          )}
        </div>

        {/* \u2500\u2500\u2500 Categories \u2500\u2500\u2500 */}
        {(() => {
          const q = searchQuery.trim().toLowerCase();
          const matchedCats = visibleCategories
            .map((cat) => {
              const matchedPerms = q
                ? cat.permissions.filter(
                    (p) =>
                      p.label.toLowerCase().includes(q) ||
                      p.key.toLowerCase().includes(q) ||
                      (p.description || '').toLowerCase().includes(q) ||
                      cat.label.toLowerCase().includes(q)
                  )
                : cat.permissions;
              return { cat, matchedPerms };
            })
            .filter(({ matchedPerms }) => matchedPerms.length > 0);

          if (matchedCats.length === 0) {
            return (
              <div
                style={{
                  padding: 'var(--space-6)',
                  textAlign: 'center',
                  color: 'var(--tm)',
                  fontSize: 'var(--font-size-base)',
                  background: 'var(--sa)',
                  borderRadius: rem(8),
                  border: '1px dashed var(--bd)',
                  marginBottom: rem(20),
                }}
              >
                No permissions match &ldquo;{searchQuery}&rdquo;
              </div>
            );
          }

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                marginBottom: rem(20),
              }}
            >
              {matchedCats.map(({ cat, matchedPerms }) => {
                const catKeys = matchedPerms.map((p) => p.key);
                const enabledCount = catKeys.filter((k) => perms.includes(k)).length;
                const allEnabled = enabledCount === matchedPerms.length;
                const someEnabled = enabledCount > 0 && !allEnabled;
                // Search auto-expands matching categories so the user can see why they matched.
                const isExpanded = q.length > 0 || expandedCats.has(cat.id);

                return (
                  <div
                    key={cat.id}
                    style={{
                      background: 'var(--sa)',
                      borderRadius: rem(8),
                      border: '1px solid var(--bd)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: rem(10),
                        padding: `${rem(10)} var(--space-3)`,
                        borderBottom: isExpanded ? '1px solid var(--bd)' : 'none',
                      }}
                    >
                      {/* Master checkbox toggles enable for the WHOLE category. We
                           stop propagation so the click doesn't also toggle
                           expansion \u2014 separation of intent prevents double-action
                           confusion. */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!readOnly) toggleCategory(cat.id);
                        }}
                        role="checkbox"
                        aria-checked={someEnabled && !allEnabled ? 'mixed' : allEnabled}
                        aria-disabled={readOnly}
                        aria-label={
                          allEnabled ? 'Disable all in category' : 'Enable all in category'
                        }
                        style={{
                          ...checkboxStyle(allEnabled),
                          background: allEnabled
                            ? 'var(--ac)'
                            : someEnabled
                              ? 'var(--ac)'
                              : 'transparent',
                          opacity: someEnabled && !allEnabled ? 0.5 : readOnly ? 0.6 : 1,
                          cursor: readOnly ? 'default' : 'pointer',
                        }}
                        title={
                          readOnly
                            ? 'Site Admin always holds every permission'
                            : allEnabled
                              ? 'Disable all in category'
                              : 'Enable all in category'
                        }
                      >
                        {allEnabled ? (
                          <Icon icon={Check} em={1.1} strokeWidth={3} />
                        ) : someEnabled ? (
                          <Icon icon={Minus} em={1.1} strokeWidth={3} />
                        ) : (
                          ''
                        )}
                      </div>

                      {/* Name + chevron \u2014 clicking this region expands/collapses */}
                      <div
                        onClick={() => toggleCatExpansion(cat.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          flex: 1,
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 'var(--space-3)',
                            color: 'var(--tm)',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform .15s ease',
                          }}
                          aria-hidden
                        >
                          <Icon icon={ChevronRight} em={0.9231} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                              fontWeight: 700,
                              color: 'var(--tx)',
                            }}
                          >
                            {cat.label}
                          </div>
                          <div
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--tm)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cat.description}
                          </div>
                        </div>
                      </div>

                      {/* Count badge */}
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 700,
                          color: enabledCount === 0 ? 'var(--tm)' : 'var(--ac)',
                          background:
                            enabledCount === 0
                              ? 'transparent'
                              : 'color-mix(in srgb, var(--ac) 12%, transparent)',
                          padding: `${rem(3)} var(--space-2)`,
                          borderRadius: rem(999),
                          border:
                            enabledCount === 0
                              ? '1px solid var(--bd)'
                              : '1px solid color-mix(in srgb, var(--ac) 40%, transparent)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {enabledCount}/{matchedPerms.length}
                      </span>
                    </div>

                    {/* Permissions grid */}
                    {isExpanded && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                        {matchedPerms.map((p) => {
                          const checked = perms.includes(p.key);
                          return (
                            <div
                              key={p.key}
                              onClick={() => {
                                if (!readOnly) togglePerm(p.key);
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-2) var(--space-3)',
                                cursor: readOnly ? 'default' : 'pointer',
                                borderTop: '1px solid var(--bd)',
                                transition: 'background .1s',
                              }}
                              onMouseEnter={(e) => {
                                if (!readOnly)
                                  e.currentTarget.style.background =
                                    'color-mix(in srgb, var(--ac) 4%, transparent)';
                              }}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                            >
                              <div
                                role="checkbox"
                                aria-checked={checked}
                                aria-label={p.label}
                                style={{ ...checkboxStyle(checked), marginTop: rem(2) }}
                              >
                                {checked ? <Icon icon={Check} em={1.1} strokeWidth={3} /> : ''}
                              </div>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                                    color: 'var(--tx)',
                                    fontWeight: 500,
                                  }}
                                >
                                  {p.label}
                                </div>
                                {p.description && (
                                  <div
                                    style={{
                                      fontSize: 'var(--font-size-sm)',
                                      color: 'var(--tm)',
                                      marginTop: rem(2),
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {p.description}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--tm)',
                                    fontFamily: "'JetBrains Mono', monospace",
                                    marginTop: rem(2),
                                    opacity: 0.6,
                                  }}
                                >
                                  {p.key}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-3) 0' }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Role'}
          </button>
          {editing.id && (
            <button onClick={() => remove(editing.id)} style={btnDanger}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Role list view ───
  // The summary used to be 9 hard-coded columns. As the catalog grew to
  // 152 keys / 19 categories, pinning columns no longer told a useful
  // story: half the catalog was invisible. The new design is a row-level
  // "category pip strip" — one pip per category, fill density encodes
  // how much of that category the role has. Scales as we add categories
  // without ever forcing a horizontal scroll.

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
          Define the roles and access privileges for your team. Each row shows a permission heatmap
          across {PERMISSION_CATEGORIES.length} categories. Hover any pip for detail.
        </div>
        <button onClick={() => setEditing(empty())} style={btnPrimary}>
          + Add Role
        </button>
      </div>

      {/* Legend strip - what the pip colors mean, shown once at the top */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-2) var(--space-3)',
          marginBottom: 'var(--space-2)',
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--tm)',
        }}
      >
        <span style={{ fontWeight: 600 }}>Heatmap:</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: rem(6) }}>
          <PermPip fill="none" />
          <span>None</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: rem(6) }}>
          <PermPip fill="partial" />
          <span>Partial</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: rem(6) }}>
          <PermPip fill="full" />
          <span>All</span>
        </span>
      </div>

      {loading ? (
        <RowSkeleton label="Loading roles" />
      ) : (
        <div
          style={{
            background: 'var(--sf)',
            borderRadius: rem(8),
            border: '1px solid var(--bd)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 1fr) auto 60px',
              alignItems: 'center',
              gap: 'var(--space-4)',
              padding: `${rem(10)} ${rem(14)}`,
              background: 'var(--sa)',
              borderBottom: '1px solid var(--bd)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
            }}
          >
            <span>Role</span>
            <span style={{ textAlign: 'right' }}>
              Permissions <span style={{ fontWeight: 400, opacity: 0.7 }}>by category</span>
            </span>
            <span></span>
          </div>

          {/* Rows */}
          {roles.map((r) => {
            // Site Admin's real grant is the server-side access_level bypass
            // (R3.1), not whatever snapshot of the catalog its stored `permissions`
            // row happens to carry — that row is seeded once at firm-creation and
            // is NEVER rewritten when the catalog grows (comment on
            // LEGACY_KEY_EXPANSIONS above). Reading it directly here under-counts
            // any category a later catalog addition landed in. Derive from the
            // SAME canonical source the read-only editor already uses
            // (`perms = readOnly ? ALL_PERMISSIONS : ...` above) so a required
            // role's pips can never silently fall behind the live catalog.
            const required = isRequired(r);
            const rp = new Set(required ? ALL_PERMISSIONS : r.permissions || []);
            const totalEnabled = required
              ? ALL_PERMISSIONS.length
              : (r.permissions || []).filter((p) => p !== '*').length;
            const isWildcard = required || (r.permissions || []).includes('*');
            return (
              <div
                key={r.id}
                onClick={() => setEditing({ ...r })}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(180px, 1fr) auto 60px',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  padding: `var(--space-3) ${rem(14)}`,
                  borderBottom: '1px solid var(--bd)',
                  cursor: 'pointer',
                  transition: 'background .1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
              >
                {/* Name + count */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      color: 'var(--tx)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{r.name}</span>
                    {isRequired(r) && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--tm)',
                          fontWeight: 400,
                        }}
                      >
                        required
                      </span>
                    )}
                    {isWildcard && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.05em',
                          padding: `${rem(2)} ${rem(6)}`,
                          borderRadius: 'var(--radius-sm)',
                          background: 'rgba(var(--acr),0.15)',
                          color: 'var(--ac)',
                        }}
                      >
                        Site Admin
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginTop: rem(2),
                    }}
                  >
                    {isWildcard
                      ? 'All current and future permissions'
                      : `${Math.min(totalEnabled, ALL_PERMISSIONS.length)} of ${ALL_PERMISSIONS.length} permissions`}
                  </div>
                </div>

                {/* Pip strip */}
                <div
                  style={{
                    display: 'inline-flex',
                    gap: rem(3),
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: rem(480),
                  }}
                >
                  {visibleCategories.map((cat) => {
                    const enabled = isWildcard
                      ? cat.permissions.length
                      : cat.permissions.filter((p) => rp.has(p.key)).length;
                    const total = cat.permissions.length;
                    const fill: 'none' | 'partial' | 'full' =
                      enabled === 0 ? 'none' : enabled >= total ? 'full' : 'partial';
                    return (
                      <PermPip
                        key={cat.id}
                        fill={fill}
                        title={`${cat.label}: ${enabled} of ${total}`}
                      />
                    );
                  })}
                </div>

                {/* Action */}
                <div style={{ textAlign: 'right' }}>
                  {!isRequired(r) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(r.id);
                      }}
                      style={btnDanger}
                      title="Delete role"
                      aria-label={`Delete role ${r.name}`}
                    >
                      <Icon icon={Trash2} em={1.1667} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {roles.length === 0 && (
            <EmptyState
              title="No roles yet"
              subtitle='Click "Add Role" or default roles will auto-seed on first load.'
            />
          )}
        </div>
      )}
    </div>
  );
}

/** A single category pip in the role-list heatmap. Uses the app accent
 *  color so the strip reads as filled vs empty rather than introducing
 *  a third hue. */
function PermPip({ fill, title }: { fill: 'none' | 'partial' | 'full'; title?: string }) {
  const variant: Record<typeof fill, React.CSSProperties> = {
    none: {
      background: 'transparent',
      border: '1px solid var(--bd)',
    },
    partial: {
      background: 'color-mix(in srgb, var(--ac) 30%, transparent)',
      border: '1px solid color-mix(in srgb, var(--ac) 60%, transparent)',
    },
    full: {
      background: 'var(--ac)',
      border: '1px solid var(--ac)',
    },
  };
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        width: rem(14),
        height: rem(18),
        borderRadius: rem(3),
        flexShrink: 0,
        cursor: title ? 'help' : 'default',
        ...variant[fill],
      }}
    />
  );
}

export { RolesTab };
